
import { defineComponent, SetupContext, PropType, ref, computed, onMounted, ComponentPublicInstance } from "vue";
import { FridgeDetailsDoorOpenInfoDailyItemTime, FridgeDetailsDoorMonitorWeeklyItemCautionStyle } from "@/types/fridgeDetails"
import NativeClient from "@/client/native-client";
import { createYAxisScales } from "@/utils/fridgeDetails-utils";

export default defineComponent({
  name: "FridgeDoorMonitorDailyItem",
  props: {
    /** ドア開閉情報 */
    list: {
      type: Array as PropType<FridgeDetailsDoorOpenInfoDailyItemTime[]>,
      required: true
    }
  },
  setup(props, context: SetupContext) {
    NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem setup start");

    const noticeText = "ドアが5分以上開いていた場合に表示されます";
    const descriptionText = "・本サービスは監視サービスではありません\n・開閉検知対象のドアは機種により異なります\n・グラフへの反映は少し時間がかかります\n・詳細は、アプリ取説をご確認ください"

    // グラフのスケール（x軸は、25等分して、3時間毎にスケールを表示）
    const yAxisTitle = "(回)"
    const xAxisScales = ["0", "", "", "3", "", "", "6", "", "", "9", "", "", "12", "", "", "15", "", "", "18", "", "", "21", "", "", "24", "" ];
    const xAxisTitle = "(時)";
    const baseCanvasSize = { width: 320, height: 240 };
    const canvasSize = computed(() => {
      const scale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem devicePixelRatio = " + scale);
      return { width: baseCanvasSize.width * scale, height: baseCanvasSize.height * scale };
    });
    const calcMaxValue = () => {
      // 回数のMax値を求める
      let maxValue = 0;
      if (props.list.length > 0) {
        maxValue = props.list.map(element => element.open_count).reduce((a, b) => Math.max(a, b), -Infinity);
        if (maxValue > 600) {
          // 600超えるならば。600にしておく
          maxValue = 600;
        }
      }
      return maxValue;
    }
    const calsYAxisScales = () => {
      if (props.list.length > 0) {
        const maxValue = calcMaxValue();
        const retValue = createYAxisScales(maxValue);
        NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem calcMaxYAxisScales maxValue = " + maxValue + ", scales = " + JSON.stringify(retValue));
        return retValue;
      } 
      return [];
    };
    const yAxisScales = computed(() => {
      const result = calsYAxisScales();
      return result;
    });

    // グラフ描画
    const canvas = ref<HTMLCanvasElement>();
    const drawGraph = () => {
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem drawGraph start");
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem window.innerWidth = " + window.innerWidth);
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem window.outerWidth = " + window.outerWidth);
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem window.screen.width = " + window.screen.width);
      // 実行判定
      if (props.list.length == 0) {
        NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem drawGraph list no element");
        return;
      }
      const scales = calsYAxisScales();
      const maxValue = Math.max(...scales);
      if (maxValue <= 0) {
        // 配列無なので描画できない
        NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem no maxValue");
        return;
      }
      if (canvas.value === undefined) {
        NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem drawGraph canvas.value is undefined");
        return;
      }
      const targetContext = canvas.value.getContext("2d");
      if (targetContext == null) {
        NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem drawGraph targetContext is null");
        return;
      }
      const screenScale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem drawGraph = " + screenScale);
      const realCanvasSize = { width: baseCanvasSize.width * screenScale, height: baseCanvasSize.height * screenScale };
      targetContext.clearRect(0, 0, realCanvasSize.width, realCanvasSize.height);

      // 定数設定
      const originPosition = { x: 20 * screenScale, y: realCanvasSize.height - 16 * screenScale };
      const barWidth = 6 * screenScale;
      const startXAxis = 9 * screenScale + originPosition.x;
      const intervalXAxis = 4.16 * screenScale;
      const maxBarHeight = 205 * screenScale;
      const strokeColor = "#707070";
      const barFillStyle = "rgba(93,214,202,1.0)";
      const linwWidth = screenScale;

      // Y軸
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y);
      targetContext.lineTo(originPosition.x, 0);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linwWidth;
      targetContext.stroke();
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem drawGraph Y axis start = (" + originPosition.x + ", " + originPosition.y + "), end = (" + originPosition.x + ", " + 0 + ")");

      // X軸
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y);
      targetContext.lineTo(realCanvasSize.width - 20 * screenScale, originPosition.y);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linwWidth;
      targetContext.stroke();

      // 描画
      let currentXPosition = startXAxis;
      for (const item of props.list) {
        let drawHeight = (item.open_count / maxValue) * maxBarHeight;
        if (drawHeight > maxBarHeight) {
          drawHeight = maxBarHeight;
        }
        targetContext.fillStyle = barFillStyle;
        targetContext.fillRect(currentXPosition, originPosition.y - drawHeight, barWidth, drawHeight);

        // 次の位置へ
        currentXPosition = currentXPosition + barWidth + intervalXAxis;
      }
    };

    // 画面が小さい場合の調整
    const styleGraphArea = computed(() => {
      if (window.innerWidth <= 320) {
        return { 'transform': 'scale(0.9)' };
      }
      return { 'transform': 'scale(1.0)' };
    });

    onMounted(() => {
      drawGraph();
    });

    // HorizontalPagingのイベントをキャンセル
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    let currentYPosition = 0;
    let currentXPosition = 0;
    const touchMainStart = (event: TouchEvent) => {
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem touchMainStart");
      currentYPosition = event.touches[0].clientY
      currentXPosition = event.touches[0].clientX
    };
    const touchMainMove = (event: TouchEvent)=> {
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem touchMainMove");
      const moveY = event.touches[0].pageY - currentYPosition;
      const moveX = event.touches[0].pageX - currentXPosition;
      currentYPosition = event.touches[0].pageY;
      currentXPosition = event.touches[0].pageX;
      if (scroll.value != undefined && Math.abs(moveY) > 0 && Math.abs(moveX) < 10) {
        const topPosition = scroll.value!.scrollTop - moveY;
        NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem touchMainMove topPosition = " + topPosition);
        scroll.value!.scrollTo({ top: topPosition, left: 0 });
      }
    }
    const touchMainEnd = (event: TouchEvent) => {
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem touchMainEnd");
    };

    // HorizontalPagingのイベントをキャンセル
    const touchStart = (event: TouchEvent) => {
      event.stopPropagation();
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem touchStart");
    };
    const touchMove = (event: TouchEvent)=> {
      event.preventDefault();
      event.stopPropagation();
    }
    const touchEnd = (event: TouchEvent) => {
      event.stopPropagation();
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem touchEnd");
    };

    // ボタン押下
    const clickCaution = () => {
      NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem clickCaution");
      context.emit("click-caution");
    };

    // ドアアラーム
    const cautionStyles = computed(() => {
      const result: Array<FridgeDetailsDoorMonitorWeeklyItemCautionStyle> = [];
      const scales = calsYAxisScales();
      const maxValue = Math.max(...scales);
      for (const item of props.list) {
        const styleElement = new FridgeDetailsDoorMonitorWeeklyItemCautionStyle(maxValue, item.open_count, item.open_alert, 20.5, 1.2, 20.6);
        result.push(styleElement);
      }
      return result;
    });

    return {
      scroll,
      touchMainStart,
      touchMainMove,
      touchMainEnd,
      styleGraphArea,
      canvas,
      canvasSize,
      yAxisTitle,
      xAxisTitle,
      yAxisScales,
      xAxisScales,
      noticeText,
      descriptionText,
      touchStart,
      touchMove,
      touchEnd,
      clickCaution,
      cautionStyles
    };
}
});
